<template>
  <div class="offer-revisions-view">
    <revision-manager
      :fields="['prices', 'tenants', 'usps', 'summaryTexts', 'media']"
      :parent="offer"
      :revisions="offer.revisions"
      @add="addRevision"
      @delete="deleteRevision"
      @edit="editRevision"
      v-if="isSuccessfullyLoaded"
    >
      <span slot="title">
        <router-link
          :to="{ name: 'offer', params: { id: offer.id }}"
        >{{offer.name}}</router-link>
      </span>

      <!-- Media -->
      <template scope="mediaProps" slot="media">
        <inheritable-value
          :inherited-value="offer.media"
          :value="mediaProps.value"
        >
          <template scope="{ value }" slot="value">
            <gallery-cell :attachments="value" />
          </template>
        </inheritable-value>
      </template>

      <!-- Prices -->
      <template scope="pricesProps" slot="prices">
        <inheritable-value
          :inherited-value="offer.prices"
          :value="pricesProps.value"
        >
          <template scope="{ value }" slot="value">
            <list :items="value" class="uk-margin-remove">
              <template scope="pricesProps" slot="item">
                <price
                  :value="pricesProps.item.value"
                  :was="pricesProps.item.type === 'WAS'"
                />
                <span v-if="pricesProps.item.organization">
                  <template v-if="isAuthenticatedAsAdmin">
                    (
                    <router-link
                      :to="{
                        name: 'edit-organization',
                        params: { id: pricesProps.item.organization.id }
                      }"
                    >{{pricesProps.item.organization.name}}</router-link>)
                  </template>
                  <span v-else>({{pricesProps.item.organization.name}})</span>
                </span>
              </template>
              <text-placeholder slot="empty" />
            </list>
          </template>
        </inheritable-value>
      </template>

      <!-- Summary Texts -->
      <template scope="summaryTextsProps" slot="summaryTexts">
        <inheritable-value
          :inherited-value="offer.summaryTexts"
          :value="summaryTextsProps.value"
        >
          <template scope="{ value }" slot="value">
            <list :items="value" class="uk-margin-remove" type="bullet">
              <text-placeholder slot="empty" />
            </list>
          </template>
        </inheritable-value>
      </template>

      <!-- Tenants -->
      <template scope="tenantsProps" slot="tenants">
        <inheritable-value
          :inherited-value="offer.tenants"
          :value="tenantsProps.value"
        >
          <template scope="{ value }" slot="value">
            <separated-list :items="value">
              <template scope="tenant" slot="item">
                <router-link
                  :to="{ name: 'edit-tenant', params: { id: tenant.item.id } }"
                >{{tenant.item.name}}</router-link>
              </template>
            </separated-list>
          </template>
        </inheritable-value>
      </template>

      <!-- USPs -->
      <template scope="uspsProps" slot="usps">
        <inheritable-value
          :inherited-value="offer.usps"
          :value="uspsProps.value"
        >
          <template scope="{ value }" slot="value">
            <list :items="value" class="uk-margin-remove" type="bullet">
              <text-placeholder slot="empty" />
            </list>
          </template>
        </inheritable-value>
      </template>
    </revision-manager>

    <div
      class="uk-text-center uk-margin-large uk-margin-large-top uk-margin-large-left uk-margin-large-right"
      v-else
    >
      <loader />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import deleteOfferRevisionMutation
  from '@/api/mutations/delete-offer-revision.graphql';
import GalleryCell from '@/components/GalleryCell';
import InheritableValue from '@/components/InheritableValue';
import List from '@/components/List';
import Loader from '@/components/Loader';
import offerWithRevisionsQuery
  from '@/api/queries/offer-with-revisions.graphql';
import Price from '@/components/Price';
import RevisionManager from '@/components/RevisionManager';
import SeparatedList from '@/components/SeparatedList';
import Submittable from '@/mixins/Submittable';
import TextPlaceholder from '@/components/TextPlaceholder';
import Toast from '@/helpers/Toast';
import View from '@/mixins/View';

export default {
  name: 'offer-revisions-view',
  mixins: [
    Submittable,
    View,
  ],
  components: {
    GalleryCell,
    InheritableValue,
    List,
    Loader,
    Price,
    RevisionManager,
    SeparatedList,
    TextPlaceholder,
  },
  computed: {
    ...mapGetters(['isAuthenticatedAsAdmin']),
    id() {
      return this.$route.params.id;
    },
    isSuccessfullyLoaded() {
      return this.isLoaded && this.offer;
    },
  },
  apollo: {
    offer: {
      query: offerWithRevisionsQuery,
      variables() {
        return { id: this.id };
      },
      fetchPolicy: 'network-only',
      loadingKey: 'loading',
      update({ offer }) {
        return offer;
      },
      skip() {
        return !this.isAuthenticated;
      },
    },
  },
  methods: {
    addRevision() {
      this.$router.push({
        name: 'new-offer-revision',
        params: { offerId: this.id },
      });
    },
    editRevision(revision) {
      this.$router.push({
        name: 'edit-offer-revision',
        params: { id: revision.id },
      });
    },
    async deleteRevision(revision) {
      await this.$apollo.mutate({
        mutation: deleteOfferRevisionMutation,
        variables: { id: revision.id },
        refetchQueries: [
          {
            query: offerWithRevisionsQuery,
            variables: { id: this.id },
          },
        ],
      });
      Toast.success(this.$t('toasts.deleted'));
    },
  },
  i18n: {
    messages: {
      de: {
        confirmations: {
          delete: 'Möchtest du die ausgewählte Revision wirklich löschen?',
        },
        toasts: {
          deleted: 'Die Revision wurde gelöscht',
        },
      },
    },
  },
};
</script>



// WEBPACK FOOTER //
// RevisionsView.vue?4ced0034